import type { CouponTextConfig } from "../shared/types.js";
import { PoweredBy } from "../shared/components/powered-by.js";

interface ContentCouponProps {
  coupon?: CouponTextConfig;
  resending: boolean;
  submitError: string;
  /** Mobile no-SMS flow: the CTA navigates to the form instead of texting a link. */
  willRedirect?: boolean;
  onResend: () => void;
}

const DEFAULT_HEADLINE = "Get $50 off your job by shooting a video of your project";
const DEFAULT_CTA_LABEL = "Get link sent to my phone";
const DEFAULT_CTA_LABEL_REDIRECT = "Continue to your quote";

export function ContentCoupon({ coupon, resending, submitError, willRedirect, onResend }: ContentCouponProps) {
  const headline = coupon?.headline?.trim() || DEFAULT_HEADLINE;
  const ctaLabel =
    coupon?.ctaLabel?.trim() ||
    (willRedirect ? DEFAULT_CTA_LABEL_REDIRECT : DEFAULT_CTA_LABEL);
  const offer = coupon?.offer?.trim();
  const subtext = coupon?.subtext?.trim();

  return (
    <div class="font-theme animate-[fv-viewIn_0.45s_cubic-bezier(0.2,0.7,0.2,1)_both]">
      {/* Body — coupon card */}
      <div class="px-6 py-6 border-b border-theme-border">
        <div class="relative flex flex-col items-center gap-3 rounded-theme border-2 border-dashed border-theme-border px-6 py-8 text-center">
          <div
            class="w-12 h-12 rounded-full flex items-center justify-center bg-skeleton"
            style={{ color: "var(--fv-theme-primary, #0a0a0a)" }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v2a2 2 0 0 1 0 4v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2a2 2 0 0 1 0-4Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
              />
              <path d="M13 4v16" stroke="currentColor" stroke-width="1.8" stroke-dasharray="2 2.5" />
            </svg>
          </div>

          {offer && (
            <div
              class="text-[34px] font-bold leading-none tracking-[-0.02em]"
              style={{ color: "var(--fv-theme-primary, #0a0a0a)" }}
            >
              {offer}
            </div>
          )}

          <h2 class="m-0 text-[19px] font-semibold leading-7 text-theme-fg">
            {headline}
          </h2>

          {subtext && (
            <p class="m-0 text-sm font-normal leading-5 text-theme-muted-fg">
              {subtext}
            </p>
          )}
        </div>
      </div>

      {/* Footer — CTA */}
      <div class="px-6 py-6">
        {submitError && (
          <div class="mb-3 text-[13px] text-theme-destructive text-center">{submitError}</div>
        )}
        <button
          type="button"
          class="flex items-center justify-center gap-2 w-full h-9 px-4 border-0 rounded-3xl text-sm font-medium cursor-pointer font-[inherit] transition-[filter] duration-150 ease-out hover:not-disabled:brightness-90 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            background: "var(--fv-theme-primary, #0a0a0a)",
            color: "var(--fv-theme-primary-foreground, #fafafa)",
          }}
          disabled={resending}
          onClick={onResend}
        >
          {resending && (
            <div class="w-4 h-4 border-2 border-white/30 border-t-current rounded-full animate-[fv-spin_0.6s_linear_infinite]" />
          )}
          <span>{resending ? (willRedirect ? "Opening..." : "Sending...") : ctaLabel}</span>
        </button>
        <PoweredBy />
      </div>
    </div>
  );
}
